import ActivitiesChapterI, { type ActivityEntry } from './ActivityChapter.interface';
import { shuffle } from 'lodash';
import md5 from 'md5';
import { getLockedSectionIdx } from '../../helpers/use-open-section';

function hasSection() {
    return getLockedSectionIdx() !== null;
}

export function getEntries(data: ActivitiesChapterI): Array<ActivityEntry> {
    const isEditor = hasSection();

    const { title, media } = data;
    const autoEntries = [
        ...['nrc', 'blend'].includes(data.autoEntryType) ? data.autoEntries.nrc : [],
        ...['ntc', 'blend'].includes(data.autoEntryType) ? data.autoEntries.ntc : [],
        ...['locked'].includes(data.autoEntryType) ? data.autoEntries.locked : [],
    ];
    const timeAware = isEditor ? autoEntries : timeRelevantEntries(data.autoEntryType, autoEntries);
    const randomized = isEditor || data.autoEntryType === 'locked' ? timeAware : shuffle(timeAware);
    const limited = randomized.slice(0, isEditor ? undefined : 3);

    const rawSections = data?.entries ?? [];
    const filtered = rawSections.filter(item => item);

    const combined = [
        ...filtered,
        ...limited,
    ].slice(0, isEditor ? undefined : 4);

    return [
        {
            chapter_title: title,
            event_type: 'intro',
            media: [media],
            subtitle: '',
            title,
        },
        ...combined.map((i) => ({ ...i, chapter_title: title })),
    ];
}

const DAY_SEGMENTS = {
    MORNING: [0, 10],
    MIDDAY: [10, 15],
    AFTERNOON: [15, 24],
};

function timeRelevantEntries(type: ActivitiesChapterI['autoEntryType'], entries: Array<ActivityEntry>): Array<ActivityEntry> {
    if (type === 'locked') {
        return segmentDailyShuffle(entries);
    }

    const currentHour = new Date().getHours();

    let period = '';
    if (currentHour >= DAY_SEGMENTS.MORNING[0] && currentHour < DAY_SEGMENTS.MORNING[1]) {
        period = 'morning';
    } else if (currentHour >= DAY_SEGMENTS.MIDDAY[0] && currentHour < DAY_SEGMENTS.MIDDAY[1]) {
        period = 'midday';
    } else if (currentHour >= DAY_SEGMENTS.AFTERNOON[0] && currentHour < DAY_SEGMENTS.AFTERNOON[1]) {
        period = 'afternoon';
    }

    return entries.filter(entry => entry.time === period);
}

/**
 * Number of entries to show per day segment
 */
const LIMIT_ENTRIES_PER_SEGMENT = 3;

/**
 * Get the expiration date for the end of a given day segment
 */
function getSegmentEndExpiration(segment: keyof typeof DAY_SEGMENTS): Date {
    const expiration = new Date();
    const segmentEndHour = DAY_SEGMENTS[segment][1];
    expiration.setHours(segmentEndHour, 0, 0, 0);

    // If we're already past the end hour for this segment, use end of day
    if (expiration < new Date()) {
        return getEndOfDayExpiration();
    }

    return expiration;
}

/**
 * Get the expiration date for the end of the current day
 */
function getEndOfDayExpiration(): Date {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
}

/**
 * Determine which segment of the day we're currently in
 */
function getCurrentDaySegment(): keyof typeof DAY_SEGMENTS {
    const currentHour = new Date().getHours();

    if (currentHour >= DAY_SEGMENTS.MIDDAY[0] && currentHour < DAY_SEGMENTS.MIDDAY[1]) {
        return 'MIDDAY';
    } else if (currentHour >= DAY_SEGMENTS.AFTERNOON[0]) {
        return 'AFTERNOON';
    }

    return 'MORNING';
}

/**
 * Build a stable content fingerprint for a pool of entries: md5 of the sorted
 * titles. Backend content changes (title added/removed/renamed) change the hash
 * and bust the cache; volatile fields (media/signed URLs) and reordering do not.
 */
function getContentHash(entries: ActivityEntry[]): string {
    return md5(entries.map(entry => entry.title).sort().join('|'));
}

/**
 * Get stored entries from local storage
 */
function getStoredEntries(key: string): { entries: ActivityEntry[], expiration: string, segment?: string, contentHash?: string } | null {
    const storedData = localStorage.getItem(key);
    if (!storedData) return null;

    const data = JSON.parse(storedData);

    // Check if the data is expired
    if (new Date(data.expiration) <= new Date()) {
        return null;
    }

    return data;
}

/**
 * Store entries to local storage with expiration
 */
function storeEntries(key: string, entries: ActivityEntry[], expiration: Date, segment?: keyof typeof DAY_SEGMENTS, contentHash?: string): void {
    const data = {
        entries,
        expiration: expiration.toISOString(),
        ...(segment && { segment }),
        ...(contentHash && { contentHash })
    };

    localStorage.setItem(key, JSON.stringify(data));
}

/**
 * Shuffle an array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

/**
 * Check for the current time section and return entries for that segment.
 * Entries are stored and managed in local storage to ensure appropriate distribution.
 * Ensures we track all displayed entries in the used bucket throughout the day.
 */
function segmentDailyShuffle(entries: Array<ActivityEntry>): Array<ActivityEntry> {
    // Storage keys
    const CURRENT_SEGMENT_KEY = 'activity_entries_current_segment_entries';
    const USED_ENTRIES_KEY = 'activity_entries_used_entries';

    // Get current day segment
    const currentSegment = getCurrentDaySegment();

    // Fingerprint the incoming (API-provided) content so backend changes bust the cache
    const currentHash = getContentHash(entries);

    // Get previously used entries (will be null if expired at end of day)
    const usedEntries = getStoredEntries(USED_ENTRIES_KEY);
    const currentUsedEntries = usedEntries?.entries || [];

    // Try to get existing entries for current segment
    const segmentData = getStoredEntries(CURRENT_SEGMENT_KEY);

    // If the cached content no longer matches the API content, discard the cache and regenerate
    if (segmentData && segmentData.contentHash !== currentHash) {
        return generateNewSegmentEntries(entries, currentUsedEntries, currentSegment, CURRENT_SEGMENT_KEY, currentHash);
    }

    // If we have segment data but it's for a different segment, or if the segment data is null but
    // we had a previous segment's data (expired), we need to handle the transition
    if (segmentData) {
        // We have some stored entries
        if (segmentData.segment === currentSegment) {
            // Still in the same segment, return entries without modification (idempotent)
            return segmentData.entries;
        } else {
            // Segment has changed - move current entries to used bucket
            const previousSegmentEntries = segmentData.entries;

            // Add previous segment entries to used entries
            const updatedUsedEntries = [...currentUsedEntries, ...previousSegmentEntries];

            // Store updated used entries with end-of-day expiration
            storeEntries(USED_ENTRIES_KEY, updatedUsedEntries, getEndOfDayExpiration());

            // Generate new entries for the current segment
            return generateNewSegmentEntries(entries, updatedUsedEntries, currentSegment, CURRENT_SEGMENT_KEY, currentHash);
        }
    } else {
        // Check if there's data in localStorage even if expired, to handle manual expiration
        const rawStoredData = localStorage.getItem(CURRENT_SEGMENT_KEY);
        if (rawStoredData) {
            try {
                // There was data but it's expired
                const expiredData = JSON.parse(rawStoredData);
                if (expiredData && expiredData.entries) {
                    // Add the expired entries to the used bucket
                    const expiredEntries = expiredData.entries;
                    const updatedUsedEntries = [...currentUsedEntries, ...expiredEntries];

                    // Store updated used entries with end-of-day expiration
                    storeEntries(USED_ENTRIES_KEY, updatedUsedEntries, getEndOfDayExpiration());

                    // Generate new entries for the current segment
                    return generateNewSegmentEntries(entries, updatedUsedEntries, currentSegment, CURRENT_SEGMENT_KEY, currentHash);
                }
            } catch (e) {
                // Invalid JSON, ignore and continue with normal flow
                console.error("Error processing expired entries:", e);
            }
        }

        // No stored entries or couldn't process expired ones - generate new ones
        return generateNewSegmentEntries(entries, currentUsedEntries, currentSegment, CURRENT_SEGMENT_KEY, currentHash);
    }
}

/**
 * Generate new entries for a segment, ensuring no duplicates from used entries
 * and limiting to LIMIT_ENTRIES_PER_SEGMENT
 */
function generateNewSegmentEntries(
    allEntries: ActivityEntry[],
    usedEntries: ActivityEntry[],
    segment: keyof typeof DAY_SEGMENTS,
    storageKey: string,
    contentHash: string
): ActivityEntry[] {
    // Create a set of used titles for efficient lookup
    const usedEntryTitles = new Set(usedEntries.map(entry => entry.title));

    // Filter out entries that have already been used
    const availableEntries = allEntries.filter(entry => !usedEntryTitles.has(entry.title));

    // If we have no available entries left, use all entries and re-shuffle
    const entriesToUse = availableEntries.length > 0 ? availableEntries : allEntries;

    // Shuffle the available entries
    const shuffledEntries = shuffleArray(entriesToUse);

    // Limit the number of entries to LIMIT_ENTRIES_PER_SEGMENT
    const limitedEntries = shuffledEntries.slice(0, LIMIT_ENTRIES_PER_SEGMENT);

    // Store the limited entries for this segment with appropriate expiration
    storeEntries(
        storageKey,
        limitedEntries,
        getSegmentEndExpiration(segment),
        segment,
        contentHash
    );

    return limitedEntries;
}
